import * as React from 'react';
import { observer } from 'mobx-react';
import SettingToggle from 'components/Common/settingToggle';
import CollapseIcon from 'components/Common/collapseIcon';
import { Options, Tooltip, Option, Input } from '@payscale/design';
import { ICycleBudgetField } from 'typings/cycleBudgetField';
import { CycleStatus } from 'typings/enums';
import * as actions from 'nomst/Actions/configurations';
import './advancedCycleSettings.scss';

export interface IAdvancedCycleSettingsProps {
    cycleStatus: string;
    budgetFields: ICycleBudgetField[];
    preFillDecision: boolean;
    preFillDecisionFields: string[];
    showIncreaseRecommendation: boolean;
    managerIncreaseDecision: boolean;
    managerIncreasePercentageUnder: number;
    managerIncreasePercentageOver: number;
}

export class AdvancedCycleSettings extends React.Component<IAdvancedCycleSettingsProps> {
    constructor(props: IAdvancedCycleSettingsProps) {
        super(props);
        this.handleToggleIncreaseRecommendation = this.handleToggleIncreaseRecommendation.bind(this);
        this.handleManagerIncreaseDecisionChange = this.handleManagerIncreaseDecisionChange.bind(this);
        this.handleManagerIncreasePercentageUnderChange = this.handleManagerIncreasePercentageUnderChange.bind(this);
        this.handleManagerIncreasePercentageOverChange = this.handleManagerIncreasePercentageOverChange.bind(this);
    }

    render(): React.ReactNode {
        const disabledClass = this.shouldDisablePreFillDecisions()
            ? 'setting-prefill-decision--disabled'
            : '';
        return (
            <CollapseIcon
                headline="Advanced Settings"
                startOpen={false}
                className="advanced-cycle-settings"
            >
                <SettingToggle
                    body={this.renderPreFillDecisions()}
                    settingId="prefillDecisions"
                    settingName={this.getPreFillSettingName()}
                    initialValue={this.props.preFillDecision}
                    onChange={this.handleTogglePreFillDecisionChange}
                    className={`setting-prefill-decision ${disabledClass}`}
                />
                <SettingToggle
                    body={this.renderIncreaseRecommendation()}
                    className="setting-increase-recommendation"
                    settingId="increaseRecommendation"
                    settingName="Increase Recommendation"
                    initialValue={this.props.showIncreaseRecommendation}
                    onChange={this.handleToggleIncreaseRecommendation}
                />
            </CollapseIcon>
        );
    }

    private getPreFillSettingName(): React.ReactNode {
        const title = 'Pre-fill Decision Fields';
        const tooltipText = 'Decision fields cannot be pre-filled when proration rules are on.';
        return this.props.cycleStatus === CycleStatus.Pending ?
            (
                <>
                    {title}
                    <Tooltip
                        hoverText={tooltipText}
                        tooltipText=""
                        display="icon"
                        className="setting-prefill-decision__tooltip"
                        id="setting-prefill-decision-tooltip"
                    />
                </>
            ) : title;
    }

    private renderPreFillDecisions(): React.ReactNode {
        return (
            <div className="pre-fill-decisions">
                <div className="pxl-form-group pre-fill-decisions__fields">
                    <p className="pre-fill-decisions__description">
                        Select fields to be pre-filled with recommendations. Managers may adjust if needed.
                    </p>
                    <Options
                        id="amountDecisionFields"
                        options={this.getPreFillDecisionOptions()}
                        onChange={this.handlePreFillDecisionFieldsChange}
                        syncOptions={false}
                        value={this.getSelectedPreFillDecisionOptions()}
                        type="pills"
                        className="pxl-pill--secondary"
                    />
                </div>
                <p className="pre-fill-decisions__warning">
                    Setting cannot be changed once the cycle has been opened.
                </p>
            </div>
        );
    }

    private renderIncreaseRecommendation(): React.ReactNode {
        const { managerIncreaseDecision, managerIncreasePercentageUnder, managerIncreasePercentageOver } = this.props;
        return (
            <div className="increase-recommendation">
                <p>
                    Provides managers an increase recommendation based on Insight Lab&apos;s Merit Matrix.
                </p>
                <div className="increase-recommendation__bumpers-checkbox">
                    <Option
                        id="managerIncreaseDecisionCheckbox"
                        checked={managerIncreaseDecision}
                        onChange={this.handleManagerIncreaseDecisionChange}
                        className="pxl-option--secondary"
                    />
                    <p>
                        Specify the percentage points managers are allowed to go over or under a recommendation
                    </p>
                </div>
                {managerIncreaseDecision && <>
                    <div className="increase-recommendation__parameters">
                        <div className="increase-recommendation__bumper-percentage">
                            <label htmlFor="managerIncreasePercentageUnder">Max % Under</label>
                            <Input
                                className="increase-recommendation__percentage-under"
                                disabled={!managerIncreaseDecision}
                                id="managerIncreasePercentageUnder"
                                min="0"
                                onChange={this.handleManagerIncreasePercentageUnderChange}
                                type="number"
                                value={String(managerIncreasePercentageUnder)}
                            />
                        </div>
                        <div className="increase-recommendation__bumper-percentage">
                            <label htmlFor="managerIncreasePercentageOver">Max % Over</label>
                            <Input
                                className="increase-recommendation__percentage-over"
                                disabled={!managerIncreaseDecision}
                                id="managerIncreasePercentageOver"
                                min="0"
                                onChange={this.handleManagerIncreasePercentageOverChange}
                                type="number"
                                value={String(managerIncreasePercentageOver)}
                            />
                        </div>
                    </div>
                    <p className="increase-recommendation__example">
                        Example: If the suggested increase recommendation is 3% and you allow a max of 1% Under 
                        and 1% Over, a manager can specify as little as 2% or up to 4% increase for an employee
                    </p>
                </>}
            </div>
        );
    }

    private getPreFillDecisionOptions(): string[] {
        const { budgetFields } = this.props;
        if (!budgetFields) {
            return [];
        }
        return Array.from(
            new Set(budgetFields.filter(field => field.shouldShowOriginalBudget).map(field => field.title)));
    }

    private getSelectedPreFillDecisionOptions(): string[] {
        const { budgetFields, preFillDecisionFields } = this.props;
        if (!budgetFields) {
            return [];
        }
        return budgetFields
            .filter(field => preFillDecisionFields.indexOf(field.amountCode) > -1)
            .map(field => field.title);
    }

    // Pre-fill does not play nicely with proration, so if there's a proration rule we do not allow pre-filling
    private shouldDisablePreFillDecisions(): boolean {
        const { cycleStatus, budgetFields } = this.props;
        const prorationRuleExists = budgetFields?.find(field => field.prorationRule != null) !== undefined;
        return cycleStatus !== CycleStatus.Pending || prorationRuleExists;
    }

    private handleTogglePreFillDecisionChange(id: string, value: boolean): Promise<void>  {
        return actions.togglePreFillDecision(value);
    }

    private handlePreFillDecisionFieldsChange(_: undefined, values: string[]): Promise<void>  {
        return actions.setPreFillDecisionFields(values);
    }

    private handleManagerIncreaseDecisionChange(_: undefined, value: IOnChangeEvent): Promise<void>  {
        return actions.toggleManagerIncreaseDecision(value.target.checked);
    }

    private handleManagerIncreasePercentageUnderChange(_: undefined, value: string): Promise<void>  {
        return actions.setManagerIncreasePercentageUnder(Number(value));
    }

    private handleManagerIncreasePercentageOverChange(_: undefined, value: string): Promise<void>  {
        return actions.setManagerIncreasePercentageOver(Number(value));
    }

    private handleToggleIncreaseRecommendation(id: string, value: boolean): Promise<void>  {
        return actions.toggleShowIncreaseRecommendation(value);
    }
}

export default observer(AdvancedCycleSettings);